import { ComponentType, ReactNode } from 'react';
import { TableCellProps } from '../../../../2-component';

export type PortfolioReports<T> = {
  fetchPortfolioReports: FetchPortfolioReports<T>;
};

export type RefreshPortfolioReport<T> = {
  refreshPortfolioReport: FetchPortfolioReport<T>;
};

export type FetchPortfolioOptimizer<T> = {
  fetchPortfolioOptimizer: {
    portfolioOptimizer: T;
    __typename: string;
  }
};

export type FetchOptimalPortfolio<T> = {
  fetchOptimalPortfolio: {
    optimalPortfolio: T;
    __typename: string;
  }
};

type FetchPortfolioReports<T> = {
  portfolioReports: T;
  totalCount: number;
  __typename: string;
};

type FetchPortfolioReport<T> = {
  portfolioReport: T;
  totalCount: number;
  __typename: string;
};

export type PortfolioReport = {
  driftRebalanceRequired: boolean;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  state: string;
  unverifiedSubTradeRequests: number;
  pendingCashCents: null | number;
  holdings: Holding[];
  id: string;
  liquidateRebalanceRequired: boolean;
  transferRebalanceRequired: boolean;
  pendingSubTradeRequests: number;
  ModelPortfolioTypes: ModelPortfolio;
  updatedAt: string;
  lastOptimizedBy: LastOptimizedBy | null;
  subAccounts: SubAccountWithFinancials[];
  ModelPortfolioTypesType: string;
  bulkTradeRunReasons: BulkTradeRunReason[];
  object: GoalSubAccount;
  __typename: string;
};

type GoalSubAccount = {
  id: string;
  __typename: 'Goal' | 'SubAccount';
  statistics: Statistics;
  goal?: Goal;
  account?: AccountWithUser;
  type?: string;
  name?: string;
  user?: UserWithOrganization;
  householdClientGroup?: null;
};

type UserWithOrganization = {
  id: string;
  firstName: string;
  lastName: string;
  entityName: null;
  organization: Organization;
  __typename: string;
};

type AccountWithUser = {
  id: string;
  type: string;
  user: UserWithEntityName;
  __typename: string;
};

type UserWithEntityName = {
  id: string;
  firstName: null | string;
  lastName: null | string;
  entityName: null;
  organization: Organization;
  __typename: string;
};

type Organization = {
  name: string;
  __typename: string;
};

type Goal = {
  id: string;
  name: null | string;
  type: string;
  householdClientGroup: null;
  __typename: string;
};

type Statistics = {
  marketValueCents: number;
  __typename: string;
};

type BulkTradeRunReason = {
  reason: string;
  total: number;
  unblockedSince: null;
  __typename: string;
};

type SubAccountWithFinancials = {
  subAccount: SubAccount;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  marketValueCents: number;
  pendingWithdrawCents: number;
  pendingCashCents: null | number;
  __typename: string;
};

type SubAccount = {
  id: string;
  state: string;
  account: Account;
  __typename: string;
};

type Account = {
  taxRank: null | null | number | number;
  type: string;
  user: User;
  __typename: string;
};

type User = {
  firstName: null | string;
  __typename: string;
};

type LastOptimizedBy = {
  id: string;
  createdAt: string;
  __typename: string;
};

type ModelPortfolio = {
  id: string;
  translatedName: TranslatedNameWithFrench;
  __typename: string;
};

type TranslatedNameWithFrench = {
  en: string;
  fr: string;
  __typename: string;
};

type Holding = {
  expectedValueCents: number;
  expectedPercentage: number;
  pendingQuantity: number;
  financialProduct: FinancialProduct;
  __typename: string;
};

type FinancialProduct = {
  id: string;
  ticker: string;
  isCash: boolean;
  currentPriceCents: number;
  name: string;
  taxRank: number;
  translatedName: TranslatedName;
  __typename: string;
};

type TranslatedName = {
  en: string;
  fr: null;
  __typename: string;
};

/**
 * Represents a column definition for a table.
 * @template T The type of the data in the table rows.
 */
export type RebalanceReportColumn<T> = {
  /** The header text for the column. */
  header: ((row: RebalanceReportColumn<T>) => ReactNode) | string;
  /** The accessor key used to retrieve the column's value from a row. */
  accessor: keyof T | string;
  sort: boolean;
  /** An optional cell rendering function. */
  cell?: (row: T, index: number) => ReactNode;
  CustomTableCell?: ComponentType<TableCellProps>;
  sorted?: 'asc' | 'desc' | undefined;
  visible?: boolean;
  pinned?: 'right' | 'left' | null;
};

export type Identifiable = {
  [key: string]: any;
  id: string | number;
};

export enum RebalancePages {
  rebalance = 'Rebalance',
  rebalance_ui = 'Rebalance UI',
  pending = 'Pending',
  requested = 'Requested',
  unsuccessful = 'Unsuccessful',
  complete = 'Complete',
}

export type RebalanceTab = {
  accessor: keyof typeof RebalancePages;
  page: RebalancePages;
  name: RebalancePages;
  notify: number;
  content: ReactNode;
};

export enum TriggerTypes {
  ANY = 1,
  BOTH,
  DRIFT,
  CASH,
}

export enum PortfolioTypes {
  ANY = 'ANY',
  GOAL = 'GOAL',
  SUB_ACCOUNT = 'SUB_ACCOUNT',
}

export enum ModelPortfolioTypes {
  ANY = 'ANY',
}

export enum PortfolioStatus {
  ANY = 'ANY',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum PendingSubTradeRequests {
  YES = 'YES',
  NO = 'NO',
  ANY = 'ANY',
}

export enum SubTradeRequestValue {
  ZERO = 'ZERO',
  GREATER_THAN_ZERO = 'GREATER_THAN_ZERO',
  ANY = 'ANY',
}

export enum RebalanceBulkTradeRunReasons {
  ANY = 'ANY',
  UNPAIRED = 'UNPAIRED',
  PAIRED = 'PAIRED',
  ILLIQUID = 'ILLIQUID',
  UNBLOCKED_BUYS = 'UNBLOCKED_BUYS',
}

export enum TradeVerification {
  ANY = 'ANY',
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
}
